import React, { useState, useEffect, useRef } from "react";
import { useAlerts } from "../hooks/useAlerts";
import AlertNotification from "./AlertNotification";
import { AlertData } from "../store/alertSlice";

const AlertManager: React.FC = () => {
  const [hasError, setHasError] = useState(false);

  const alertsData = useAlerts();
  const { alerts, error } = alertsData; // Get raw alerts directly from Redux
  const getFilteredAlerts = alertsData.getFilteredAlerts;
  const getCriticalAlerts = alertsData.getCriticalAlerts;

  const [activeNotifications, setActiveNotifications] = useState<AlertData[]>(
    [],
  );
  const maxNotifications = useRef(3); // Max notifications to show at once, using useRef

  // Ref to keep track of alerts currently managed by activeNotifications
  const processedAlertIds = useRef(new Set<string>());

  // Handle errors gracefully
  useEffect(() => {
    if (error) {
      console.warn("AlertManager: useAlerts error:", error);
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, [error]);

  // Effect to add new, un-displayed alerts
  useEffect(() => {
    if (hasError) return;

    const filtered = getFilteredAlerts(); // Current state of filtered alerts from Redux
    const newAlertsToAdd: AlertData[] = [];

    filtered.forEach((alert) => {
      // Add alerts that are not in activeNotificationIds yet
      if (!processedAlertIds.current.has(alert.id)) {
        newAlertsToAdd.push(alert);
        processedAlertIds.current.add(alert.id); // Mark as processed for this component's active list
      }
    });

    if (newAlertsToAdd.length > 0) {
      setActiveNotifications((prev) => {
        const updated = [...prev, ...newAlertsToAdd];
        return updated.slice(-maxNotifications.current);
      });
    }

    // Also, clean up activeNotificationIds for alerts that are no longer in Redux filtered list
    // This ensures `activeNotificationIds` accurately reflects what's in `activeNotifications`
    processedAlertIds.current.forEach((id: string) => {
      if (!filtered.some((alert) => alert.id === id)) {
        processedAlertIds.current.delete(id);
      }
    });
  }, [alerts, hasError, getFilteredAlerts]); // Depend on raw alerts, not activeNotifications directly

  // Play sound for critical alerts
  useEffect(() => {
    if (hasError) return;

    getCriticalAlerts().forEach((alert: AlertData) => {
      if (!alert.acknowledged && !alert.dismissed) {
        const audio = new Audio("/sounds/critical-alert.mp3");
        audio.play().catch(() => {
          console.log("Critical alert!");
        });
      }
    });
  }, [getCriticalAlerts, hasError]);

  // Don't render if there's an error
  if (hasError) {
    return null;
  }

  const { acknowledgeAlert, dismissAlert } = alertsData;

  const handleAcknowledge = (alertId: string) => {
    acknowledgeAlert(alertId);
    // Optimistically remove from display
    setActiveNotifications((prev) => {
      const updated = prev.filter((n) => n.id !== alertId);
      processedAlertIds.current.delete(alertId); // Also remove from tracking ref
      return updated;
    });
  };

  const handleDismiss = (alertId: string) => {
    dismissAlert(alertId);
    // Optimistically remove from display
    setActiveNotifications((prev) => {
      const updated = prev.filter((n) => n.id !== alertId);
      processedAlertIds.current.delete(alertId); // Also remove from tracking ref
      return updated;
    });
  };

  const handleClose = (alertId: string) => {
    // Local UI close, not necessarily dismissing the alert in Redux
    setActiveNotifications((prev) => {
      const updated = prev.filter((n) => n.id !== alertId);
      processedAlertIds.current.delete(alertId); // Also remove from tracking ref
      return updated;
    });
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-4">
      {activeNotifications.map((alert, index) => (
        <div
          key={alert.id}
          style={{
            transform: `translateY(${index * 20}px)`,
            zIndex: 1000 - index,
          }}
        >
          <AlertNotification
            alert={alert}
            onAcknowledge={handleAcknowledge}
            onDismiss={handleDismiss}
            onClose={() => handleClose(alert.id)}
            autoClose={alert.severity < 4} // Don't auto-close critical alerts
            autoCloseDelay={alert.severity >= 4 ? 10000 : 5000} // Longer delay for critical alerts
          />
        </div>
      ))}
    </div>
  );
};

export default AlertManager;
